import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  OpenInNew as OpenInNewIcon,
  PlayArrow as PlayArrowIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import {
  getPrCoverageGapLatestSummary,
  PrCoverageGapDetail,
  PrCoverageGapLatestSummary,
} from '../../api/scheduledJobs';
import {
  pollBackfillPreview,
  startBackfillPreview,
} from '../../api/contentCompletion';
import {
  BackfillFeedType,
  BackfillPreviewResult,
  CompanyContentHealth,
} from '../../types/contentCompletion';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage/ErrorMessage';
import { useAuth } from '../../hooks/useAuth';
import { useNotification } from '../../context/NotificationContext';
import BackfillPreviewDialog from '../ContentCompletionPage/BackfillPreviewDialog';
import BatchBackfillReviewDialog, {
  BatchBackfillReviewItem,
} from '../ContentCompletionPage/BatchBackfillReviewDialog';
import {
  companyFromGapDetail,
  daysBackForGap,
  feedTypeForGap,
  filterPreviewToGap,
} from './gapBackfillUtils';

const GAP_AGENT_JOB_ID = 'pr_coverage_gap_agent';

/** Display order for status accordions (actionable first). */
const STATUS_ORDER = [
  'gap',
  'not_found',
  'unreachable',
  'relocated',
  'empty_page',
  'no_feed',
  'stale_source',
  'error',
  'inactive',
  'active',
] as const;

const STATUS_LABELS: Record<string, string> = {
  gap: 'Gap (page ahead of Neo4j)',
  not_found: 'Not found (404)',
  unreachable: 'Unreachable',
  relocated: 'Relocated / URL changed',
  empty_page: 'Empty page (no dates)',
  no_feed: 'No feed configured',
  stale_source: 'Stale source (stopped posting)',
  error: 'Errors',
  inactive: 'Inactive (acquired / merged / defunct)',
  active: 'Active (in sync)',
};

const STATUS_CHIP_COLOR: Record<
  string,
  'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'
> = {
  gap: 'warning',
  not_found: 'error',
  unreachable: 'error',
  relocated: 'info',
  empty_page: 'warning',
  no_feed: 'default',
  stale_source: 'warning',
  error: 'error',
  inactive: 'default',
  active: 'success',
};

const DEFAULT_EXPANDED = new Set([
  'gap',
  'not_found',
  'unreachable',
  'relocated',
  'empty_page',
  'error',
]);

const formatDateTime = (value?: string | null) => {
  if (!value) return 'Never';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Never';
  return parsed.toLocaleString();
};

const formatDate = (value?: string | null) => {
  if (!value) return '—';
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toISOString().slice(0, 10);
};

const relativeTime = (value?: string | null) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  const diffMs = Date.now() - parsed.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays <= 0) return 'today';
  if (diffDays === 1) return 'yesterday';
  return `${diffDays} days ago`;
};

const sortDetails = (rows: PrCoverageGapDetail[]): PrCoverageGapDetail[] => {
  return [...rows].sort((a, b) => {
    const gapA = a.gap_days ?? -Infinity;
    const gapB = b.gap_days ?? -Infinity;
    if (gapA !== gapB) return gapB - gapA;
    return (a.company_name || '').localeCompare(b.company_name || '');
  });
};

const pollPreviewToCompletion = (runId: string): Promise<BackfillPreviewResult> =>
  new Promise((resolve) => {
    const stop = pollBackfillPreview(runId, (update) => {
      if (update.status !== 'running') {
        stop();
        resolve(update);
      }
    });
  });

interface PrCoverageGapPanelProps {
  /** Bump to force a refetch when the parent refreshes. */
  refreshToken?: number;
}

const PrCoverageGapPanel: React.FC<PrCoverageGapPanelProps> = ({
  refreshToken = 0,
}) => {
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const isEditorOrAdmin =
    Boolean(user?.is_superuser) ||
    user?.role === 'admin' ||
    user?.role === 'editor';

  const [summary, setSummary] = useState<PrCoverageGapLatestSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set(DEFAULT_EXPANDED));

  const [previewingCompanyId, setPreviewingCompanyId] = useState<string | null>(
    null
  );
  const [batchPreviewing, setBatchPreviewing] = useState(false);
  const [batchProgress, setBatchProgress] = useState<string | null>(null);
  const [singleReview, setSingleReview] = useState<{
    company: CompanyContentHealth;
    sourceUrl: string;
    preview: BackfillPreviewResult;
    daysBack: number;
    feedType: BackfillFeedType;
  } | null>(null);
  const [batchReviewItems, setBatchReviewItems] = useState<
    BatchBackfillReviewItem[]
  >([]);
  const [batchReviewOpen, setBatchReviewOpen] = useState(false);
  const [batchDaysBack, setBatchDaysBack] = useState(30);
  const [batchFeedType, setBatchFeedType] = useState<BackfillFeedType>('auto');
  const [savedCompanyIds, setSavedCompanyIds] = useState<Set<string>>(new Set());

  const cancelledRef = useRef(false);
  const runIdRef = useRef(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getPrCoverageGapLatestSummary(GAP_AGENT_JOB_ID);
      setSummary(data);
      if (data.default_expanded_statuses?.length) {
        setExpanded(new Set(data.default_expanded_statuses));
      }
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ||
        (err as Error)?.message ||
        'Failed to load PR coverage gap summary';
      setError(msg);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshToken]);

  useEffect(() => {
    return () => {
      cancelledRef.current = true;
      runIdRef.current += 1;
    };
  }, []);

  const detailsByStatus = useMemo(() => {
    const map = new Map<string, PrCoverageGapDetail[]>();
    for (const row of summary?.details || []) {
      const key = (row.status || 'error').toLowerCase();
      const list = map.get(key) || [];
      list.push(row);
      map.set(key, list);
    }
    for (const [key, list] of map) {
      map.set(key, sortDetails(list));
    }
    return map;
  }, [summary]);

  const gapRows = useMemo(
    () =>
      (detailsByStatus.get('gap') || []).filter(
        (row) => row.company_id && (row.source_url || '').trim()
      ),
    [detailsByStatus]
  );

  const orderedStatuses = useMemo(() => {
    const counts = summary?.status_counts || {};
    const keys = new Set([
      ...STATUS_ORDER,
      ...Object.keys(counts),
      ...Array.from(detailsByStatus.keys()),
    ]);
    return Array.from(keys)
      .filter((key) => (counts[key] ?? detailsByStatus.get(key)?.length ?? 0) > 0)
      .sort((a, b) => {
        const ia = STATUS_ORDER.indexOf(a as (typeof STATUS_ORDER)[number]);
        const ib = STATUS_ORDER.indexOf(b as (typeof STATUS_ORDER)[number]);
        const ra = ia === -1 ? 999 : ia;
        const rb = ib === -1 ? 999 : ib;
        if (ra !== rb) return ra - rb;
        return a.localeCompare(b);
      });
  }, [summary, detailsByStatus]);

  const toggle = (status: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status);
      else next.add(status);
      return next;
    });
  };

  const runPreviewForRow = async (
    row: PrCoverageGapDetail,
    options?: { quiet?: boolean }
  ): Promise<{
    company: CompanyContentHealth;
    sourceUrl: string;
    preview: BackfillPreviewResult;
    daysBack: number;
    feedType: BackfillFeedType;
  } | null> => {
    const quiet = Boolean(options?.quiet);
    const sourceUrl = (row.source_url || '').trim();
    if (!row.company_id || !sourceUrl) {
      if (!quiet) {
        showNotification('Company is missing a press-release / RSS URL', 'warning');
      }
      return null;
    }
    const company = companyFromGapDetail(row);
    const daysBack = daysBackForGap(row);
    const feedType = feedTypeForGap(row);
    const { run_id } = await startBackfillPreview({
      company_id: company.company_id,
      company_name: company.company_name,
      source_url: sourceUrl,
      feed_type: feedType,
      days_back: daysBack,
      fetch_content: true,
    });
    const raw = await pollPreviewToCompletion(run_id);
    if (raw.status === 'failure') {
      if (!quiet) {
        showNotification(raw.error || 'Preview failed', 'error');
      }
      return null;
    }
    const preview = filterPreviewToGap(raw, row.neo4j_last_pr_date);
    if (preview.press_releases_found === 0) {
      if (!quiet) {
        showNotification(
          raw.press_releases_found === 0
            ? `No press releases found for ${company.company_name}`
            : `No PRs newer than Neo4j for ${company.company_name}`,
          'warning'
        );
      }
      return null;
    }
    return { company, sourceUrl, preview, daysBack, feedType };
  };

  const handleReviewOne = async (row: PrCoverageGapDetail) => {
    if (!isEditorOrAdmin || !row.company_id) return;
    setPreviewingCompanyId(row.company_id);
    try {
      const result = await runPreviewForRow(row);
      if (result) {
        setSingleReview(result);
        showNotification(
          `Found ${result.preview.press_releases_found} missing press release(s)`,
          'success'
        );
      }
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ||
        (err as Error)?.message ||
        'Failed to preview backfill';
      showNotification(msg, 'error');
    } finally {
      setPreviewingCompanyId(null);
    }
  };

  const handleBackfillAllGaps = async () => {
    if (!isEditorOrAdmin || gapRows.length === 0) return;

    const runId = runIdRef.current + 1;
    runIdRef.current = runId;
    cancelledRef.current = false;
    setBatchPreviewing(true);
    setBatchProgress(`0 / ${gapRows.length}`);
    setBatchReviewOpen(false);
    setBatchReviewItems([]);

    const ready: BatchBackfillReviewItem[] = [];
    let maxDays = 30;
    let failures = 0;

    for (let i = 0; i < gapRows.length; i += 1) {
      if (cancelledRef.current || runIdRef.current !== runId) break;
      const row = gapRows[i];
      setBatchProgress(
        `${i + 1} / ${gapRows.length}: ${row.company_name || row.company_id}`
      );
      try {
        const result = await runPreviewForRow(row, { quiet: true });
        if (result) {
          ready.push({
            companyId: result.company.company_id,
            companyName: result.company.company_name,
            company: result.company,
            sourceUrl: result.sourceUrl,
            previewResult: result.preview,
          });
          maxDays = Math.max(maxDays, result.daysBack);
        } else {
          failures += 1;
        }
      } catch {
        failures += 1;
      }
    }

    setBatchPreviewing(false);
    setBatchProgress(null);

    if (cancelledRef.current || runIdRef.current !== runId) return;

    if (ready.length === 0) {
      showNotification(
        failures
          ? 'No missing press releases to review (previews failed or empty)'
          : 'No missing press releases to review',
        'warning'
      );
      return;
    }

    setBatchDaysBack(maxDays);
    setBatchFeedType('auto');
    setBatchReviewItems(ready);
    setBatchReviewOpen(true);
    showNotification(
      `Ready to review ${ready.length} compan${ready.length === 1 ? 'y' : 'ies'}` +
        (failures ? ` (${failures} skipped)` : ''),
      'success'
    );
  };

  const markSaved = (companyId: string) => {
    setSavedCompanyIds((prev) => new Set(prev).add(companyId));
  };

  const hasRun =
    summary &&
    summary.last_run_status !== 'never' &&
    !summary.deferred &&
    (summary.companies_processed ?? 0) > 0;

  const busy = Boolean(previewingCompanyId) || batchPreviewing;

  return (
    <Paper variant="outlined" sx={{ mt: 3 }}>
      <Box sx={{ px: 2, pt: 2, pb: 1 }}>
        <Typography variant="subtitle1" fontWeight={700}>
          PR coverage gap (latest sweep)
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          Newest press-release date on each company&apos;s live PRL / RSS page vs
          Neo4j. For gap companies, preview missing PRs, review them, and save to
          Neo4j.
        </Typography>
        {summary && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              alignItems: 'center',
              mt: 1.5,
            }}
          >
            <Chip
              size="small"
              label={summary.last_run_status || 'never'}
              color={
                summary.last_run_status === 'success'
                  ? 'success'
                  : summary.last_run_status === 'partial'
                    ? 'warning'
                    : summary.last_run_status === 'failure'
                      ? 'error'
                      : 'default'
              }
            />
            <Typography variant="body2" color="text.secondary">
              {relativeTime(summary.last_run_at)
                ? `Last run ${relativeTime(summary.last_run_at)}`
                : 'Never run'}
              {summary.last_run_at
                ? ` · ${formatDateTime(summary.last_run_at)}`
                : ''}
            </Typography>
            {summary.scope && (
              <Typography variant="body2" color="text.secondary">
                · scope {summary.scope}
              </Typography>
            )}
            {typeof summary.companies_processed === 'number' &&
              summary.companies_processed > 0 && (
                <Typography variant="body2" color="text.secondary">
                  · {summary.companies_processed} companies
                </Typography>
              )}
            <Link
              component={RouterLink}
              to="/scheduled-jobs"
              variant="body2"
              sx={{
                ml: 'auto',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              Run from Scheduled Jobs
              <OpenInNewIcon sx={{ fontSize: 14 }} />
            </Link>
          </Box>
        )}
      </Box>

      {error && (
        <Box sx={{ px: 2, pb: 2 }}>
          <ErrorMessage message={error} onDismiss={() => setError(null)} />
        </Box>
      )}

      {loading ? (
        <Box sx={{ py: 3 }}>
          <LoadingSpinner message="Loading gap sweep…" />
        </Box>
      ) : summary?.deferred ? (
        <Box sx={{ px: 2, pb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Last trigger was deferred (waiting for min_interval_days). No new
            coverage results yet.
          </Typography>
        </Box>
      ) : !hasRun ? (
        <Box sx={{ px: 2, pb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            No gap sweep results yet. Open Scheduled Jobs and run{' '}
            <strong>PR Coverage Gap Agent</strong> (health check is enough for a
            sample).
          </Typography>
        </Box>
      ) : (
        <Box sx={{ px: 1, pb: 2 }}>
          {(batchPreviewing || batchProgress) && (
            <Box sx={{ px: 1, pb: 2 }}>
              <Alert severity="info" icon={<CircularProgress size={18} />}>
                Previewing gap companies… {batchProgress}
              </Alert>
              <LinearProgress sx={{ mt: 1 }} />
            </Box>
          )}

          {orderedStatuses.map((statusKey) => {
            const count =
              summary?.status_counts?.[statusKey] ??
              detailsByStatus.get(statusKey)?.length ??
              0;
            const rows = detailsByStatus.get(statusKey) || [];
            const isExpanded = expanded.has(statusKey);
            const isGap = statusKey === 'gap';

            return (
              <Accordion
                key={statusKey}
                disableGutters
                elevation={0}
                expanded={isExpanded}
                onChange={() => toggle(statusKey)}
                sx={{
                  '&:before': { display: 'none' },
                  borderBottom: (theme) =>
                    `1px solid ${theme.palette.divider}`,
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      width: '100%',
                      pr: 1,
                    }}
                  >
                    <Chip
                      size="small"
                      label={count}
                      color={STATUS_CHIP_COLOR[statusKey] || 'default'}
                      sx={{ minWidth: 40, fontWeight: 700 }}
                    />
                    <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
                      {STATUS_LABELS[statusKey] || statusKey}
                    </Typography>
                    {isGap && isEditorOrAdmin && gapRows.length > 0 && (
                      <Button
                        size="small"
                        variant="contained"
                        color="warning"
                        startIcon={<PlayArrowIcon />}
                        disabled={busy}
                        onClick={(e) => {
                          e.stopPropagation();
                          void handleBackfillAllGaps();
                        }}
                      >
                        Backfill all gaps ({gapRows.length})
                      </Button>
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  {rows.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No company details stored for this status.
                    </Typography>
                  ) : (
                    <TableContainer sx={{ maxHeight: 360 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Company</TableCell>
                            <TableCell>Page last PR</TableCell>
                            <TableCell>Neo4j last PR</TableCell>
                            <TableCell align="right">Gap days</TableCell>
                            <TableCell>Reason</TableCell>
                            {isGap && isEditorOrAdmin && (
                              <TableCell align="right">Action</TableCell>
                            )}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {rows.map((row) => {
                            const companyId = row.company_id || '';
                            const isPreviewing =
                              previewingCompanyId === companyId;
                            const isSaved = savedCompanyIds.has(companyId);
                            const canBackfill =
                              isGap &&
                              isEditorOrAdmin &&
                              Boolean((row.source_url || '').trim());

                            return (
                              <TableRow
                                key={`${companyId}-${row.status}`}
                                hover
                              >
                                <TableCell>
                                  <Typography variant="body2" fontWeight={600}>
                                    {row.company_name || companyId || '—'}
                                  </Typography>
                                  {companyId && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      component="div"
                                    >
                                      {companyId}
                                    </Typography>
                                  )}
                                  {row.source_url && (
                                    <Link
                                      href={row.source_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      variant="caption"
                                      display="block"
                                      sx={{ wordBreak: 'break-all' }}
                                    >
                                      {row.source_url}
                                    </Link>
                                  )}
                                  {isSaved && (
                                    <Chip
                                      size="small"
                                      label="Saved this session"
                                      color="success"
                                      sx={{ mt: 0.5 }}
                                    />
                                  )}
                                </TableCell>
                                <TableCell>
                                  {formatDate(row.page_last_pr_date)}
                                </TableCell>
                                <TableCell>
                                  {formatDate(row.neo4j_last_pr_date)}
                                </TableCell>
                                <TableCell align="right">
                                  {row.gap_days != null ? row.gap_days : '—'}
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {row.error || row.reason || '—'}
                                    {row.proposed_url
                                      ? ` → ${row.proposed_url}`
                                      : ''}
                                  </Typography>
                                </TableCell>
                                {isGap && isEditorOrAdmin && (
                                  <TableCell align="right">
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      disabled={
                                        busy || !canBackfill || isSaved
                                      }
                                      onClick={() => void handleReviewOne(row)}
                                      startIcon={
                                        isPreviewing ? (
                                          <CircularProgress size={14} />
                                        ) : undefined
                                      }
                                    >
                                      {isSaved
                                        ? 'Saved'
                                        : isPreviewing
                                          ? 'Previewing…'
                                          : 'Review & backfill'}
                                    </Button>
                                  </TableCell>
                                )}
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                  {isGap && !isEditorOrAdmin && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 1, display: 'block' }}
                    >
                      Editor or admin role is required to preview and save
                      press releases.
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      )}

      {singleReview && (
        <BackfillPreviewDialog
          open
          onClose={() => setSingleReview(null)}
          company={singleReview.company}
          defaultSourceUrl={singleReview.sourceUrl}
          initialPreviewResult={singleReview.preview}
          initialDaysBack={singleReview.daysBack}
          initialFeedType={singleReview.feedType}
          reviewOnly
          onComplete={() => {
            markSaved(singleReview.company.company_id);
            setSingleReview(null);
          }}
        />
      )}

      <BatchBackfillReviewDialog
        open={batchReviewOpen}
        onClose={() => setBatchReviewOpen(false)}
        items={batchReviewItems}
        daysBack={batchDaysBack}
        feedType={batchFeedType}
        onCompanyIngested={(companyId) => markSaved(companyId)}
        onComplete={() => {
          setBatchReviewOpen(false);
          showNotification(
            'Backfill complete. Re-run the gap agent to refresh coverage status.',
            'info'
          );
        }}
      />
    </Paper>
  );
};

export default PrCoverageGapPanel;
